/* eslint-disable @typescript-eslint/no-explicit-any */
import { getStorefrontProductDocument } from "@/lib/storefront-product";
import {
  getCategoryProductsData,
  getProductBasicData,
  getProductInspirations,
  getProductTables,
  getRelatedProductsData,
  processProductImages,
} from "@/lib/prismic/products";
import { notFound } from "next/navigation";
import { use } from "react";

import type { ProductDocument } from "@/prismicio-types";

// import ArticlesGrid from "@/components/molecules/articles-grid";
import BestSellers from "@/components/molecules/best-sellers";
import TabsProduct from "@/components/molecules/tabs-product";
import ProductHero from "@/components/organisms/product-hero";
import ArchiveProduct from "./ArchiveProduct";
import OurServices from "@/components/organisms/our-services";
import TablesCollection from "@/components/molecules/tables-collection";
import { __, getPermalink } from "@/lib/i18n/functions";
// import { getPrismicLocale } from "@/lib/i18n";
import InspirationCollection from "../molecules/inspiration-collection";
import ProductJSONLD from "@/components/organisms/product-jsonld";
import { isProductOrderable } from "@/lib/product-availability";

const Product = ({ resolution }: TemplateProps) => {
  if (resolution.isArchive) return <ArchiveProduct resolution={resolution} />;

  const product: ProductDocument | null = use(getStorefrontProductDocument(resolution));
  if (!product || !product.data) return notFound();

  const { frenchProduct, isCollection, medusaProduct, parentCollection } = use(
    getProductBasicData(product, resolution)
  ); // Get basic product data

  if (!isCollection && !isProductOrderable(medusaProduct)) return notFound();
  const availableMedusaProduct = medusaProduct ?? undefined;

  const isOutOfStock =
    !frenchProduct?.data.productCollection && !isProductOrderable(availableMedusaProduct);

  const { productImages, collectionProductCategoriesFromPrismic, collectionProductsFromPrismic } = use(
    processProductImages(product, frenchProduct, availableMedusaProduct, isCollection, resolution)
  ); // Process product images and collection products data

  const { relatedProductsFromPrismic, relatedProductsCategoriesFromPrismic } = use(
    getRelatedProductsData(frenchProduct, resolution)
  ); // Get related products data

  const categoryProductsFromPrismic = use(
    relatedProductsFromPrismic?.length
      ? Promise.resolve([])
      : getCategoryProductsData(frenchProduct, frenchProduct?.uid || product.uid, resolution)
  ); // Fallback products from the same category when no manual related products are set

  const suggestedProducts = relatedProductsFromPrismic?.length ? relatedProductsFromPrismic : categoryProductsFromPrismic;
  const suggestedProductCategories = relatedProductsFromPrismic?.length ? relatedProductsCategoriesFromPrismic : null;

  const tables = use(getProductTables(frenchProduct || product, resolution.locale)); // Get product tables data
  const inspirations = use(getProductInspirations(frenchProduct || product, resolution.locale));

  const enhancedProductData = {
    prismicProduct: product,
    processedImages: productImages,
    parentCollection,
    medusaProduct: availableMedusaProduct,
  }; // Enhance product data with processed images

  return (
    <>
      <ProductJSONLD
        product={product}
        medusaProduct={availableMedusaProduct}
        images={productImages}
        locale={resolution.locale}
        isOutOfStock={isOutOfStock}
      />
      <ProductHero
        isRange={!!availableMedusaProduct?.metadata?.is_surface_based}
        productInfo={enhancedProductData}
        isOutOfStock={isOutOfStock}
        isCollection={isCollection}
        collectionProducts={collectionProductsFromPrismic}
        collectionCategories={collectionProductCategoriesFromPrismic}
        locale={resolution.locale}
        isImageFullWidth={frenchProduct?.data.image_full_size_card}
        onlyDisplayFirstImage={frenchProduct?.data?.onlyDisplayFirstImage ?? false}
      />

      <TabsProduct
        accordionClassname="space-y-1"
        className="my-4 sm:my-11"
        tabs={[
          {
            id: "description",
            question: "Description",
            answers: product.data?.informations_completes,
          },
          {
            id: "consigne",
            question: "Consigne",
            answers: product.data?.consigne,
          },
          {
            id: "questions",
            question: "Questions",
            answers: product.data?.questions?.map(({ question, answer }: any) => ({
              question,
              answer,
            })),
          },
        ]}
      />
      {/* Display manual related products, or fallback products from the same category. */}
      {suggestedProducts && suggestedProducts.length > 0 && (
        <BestSellers
          className="pb-0 md:bg-[#FBF9F899]"
          variant="payment"
          headline={__("Similar articles", resolution.locale)}
          productCategories={suggestedProductCategories}
          description=""
          linkText={__("See all products", resolution.locale)}
          link={getPermalink(resolution.locale, "catalogue")}
          products={suggestedProducts}
          locale={resolution.locale}
        />
      )}
      {/* Display collection products if the product is a collection or category products if available */}
      {/*{((collectionProductsFromPrismic && collectionProductsFromPrismic.length >= 1) ||
        categoryProductsFromPrismic) && (
        <ArticlesGrid
          headline={__("These products may interest you", resolution.locale)}
          linkText={__("See all products", resolution.locale)}
          link={getPermalink(resolution.locale, "catalogue")}
          className="max-md:my-7.5 md:py-11"
          products={(isCollection ? collectionProductsFromPrismic : categoryProductsFromPrismic) ?? []}
          locale={resolution.locale}
          collectionCategories={collectionProductCategoriesFromPrismic}
        />
      )}*/}
      <TablesCollection tables={tables} locale={resolution.locale} />
      <InspirationCollection inspirations={inspirations} locale={resolution.locale} />
      <OurServices locale={resolution.locale} />
    </>
  );
};

export default Product;

// Product.generateMetadata = async (resolution: Resolution) => {
//   if (resolution.isArchive) return ArchiveProduct.generateMetadata(resolution);

//   return false;
// };
